import { useState, useEffect } from "react";
import { useProjects } from "../hooks/useProjects.ts";
import { useStats } from "../hooks/useStats.ts";
import { useDocumentMeta } from "../hooks/useDocumentMeta.ts";
import { apiFetch, type SingleResponse } from "../lib/api.ts";
import type { Category } from "../hooks/useCategories.ts";
import { ProjectGrid } from "../components/ProjectGrid.tsx";
import { FilterBar } from "../components/FilterBar.tsx";
import { Pagination } from "../components/Pagination.tsx";
import { Spinner } from "../components/Spinner.tsx";
import { navigate } from "../lib/router.ts";

export function CategoryPage({ slug }: { slug: string }) {
  const [category, setCategory] = useState<Category | null>(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("stars");
  const [language, setLanguage] = useState("");
  const { stats } = useStats();

  useEffect(() => {
    apiFetch<SingleResponse<Category>>(`/categories/${slug}`)
      .then((res) => setCategory(res.data))
      .catch(() => navigate("/"));
  }, [slug]);

  const { projects, meta, loading } = useProjects({
    page,
    perPage: 30,
    sort,
    category: slug,
    language: language || undefined,
  });

  useEffect(() => { setPage(1); }, [sort, language, slug]);

  const startRank = (page - 1) * 30 + 1;

  useDocumentMeta({
    title: category
      ? `${category.name} - Open-Source Tools | There's an OS for That`
      : "There's an OS for That",
    description: category
      ? `Browse ${meta.total} open-source ${category.name.toLowerCase()} projects. ${category.description}`
      : undefined,
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {category && (
        <div className="mb-8">
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate("/"); }}
            className="text-xs text-text-muted hover:text-accent transition-colors mb-4 inline-block"
          >
            &larr; back
          </a>
          <h1 className="text-lg font-medium text-text-primary">{category.name}</h1>
          <p className="text-xs text-text-secondary mt-1">Browse {meta.total} open-source {category.name.toLowerCase()} projects. {category.description}</p>
        </div>
      )}

      <FilterBar
        sort={sort}
        onSortChange={setSort}
        language={language}
        onLanguageChange={setLanguage}
        languages={stats?.languages ?? []}
      />

      {loading ? (
        <Spinner />
      ) : (
        <>
          <ProjectGrid projects={projects} startRank={startRank} />
          <Pagination page={page} totalPages={meta.total_pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
